/**
 * ContentVisual — Smart Article Visual Component
 *
 * Renders either:
 *   - "infographic" → CSS animated stats + key insights grid
 *   - "animation"   → Remotion Player with BlogAnimation composition
 *
 * The LLM decides which type via animation_storyboard.visual_type.
 * User can manually switch via toggle.
 */
import { useState } from 'react';
import { Player } from '@remotion/player';
import { BlogAnimation } from './remotion/BlogAnimation';
import { Film, BarChart2, RefreshCw, Sparkles } from 'lucide-react';

// ── Animated Infographic (CSS) ─────────────────────────────────────────────────
function AnimatedInfographic({ storyboard, themeColor }) {
  const insights = [
    storyboard.insight1,
    storyboard.insight2,
    storyboard.insight3,
  ].filter(Boolean);

  // Generate stat cards from the storyboard data
  const statCards = [
    { value: storyboard.stat?.match(/\d+[%xXBMK$]?/)?.[0] || '73%', label: 'Key Metric', icon: '📊' },
    { value: '3x', label: 'Impact', icon: '⚡' },
    { value: '2026', label: 'Trending', icon: '🚀' },
    { value: '#1', label: 'Priority', icon: '🎯' },
  ];

  return (
    <div style={{
      background: 'linear-gradient(145deg, #0c0c1a, #0a0a14)',
      borderRadius: 16,
      padding: 24,
      border: `1px solid ${themeColor}20`,
      boxShadow: `0 8px 40px ${themeColor}08`,
    }}>
      {/* Headline */}
      <h3 style={{
        color: themeColor,
        fontSize: 15,
        fontWeight: 900,
        marginBottom: 20,
        textAlign: 'center',
        letterSpacing: '-0.02em',
      }}>
        {storyboard.title}
      </h3>

      {/* Stat cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 10,
        marginBottom: 16,
      }}>
        {statCards.map((stat, i) => (
          <div
            key={i}
            className="stat-card-animate"
            style={{
              animationDelay: `${i * 0.12}s`,
              background: `${themeColor}10`,
              border: `1px solid ${themeColor}28`,
              borderRadius: 12,
              padding: '12px 6px',
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: 18, marginBottom: 4 }}>{stat.icon}</div>
            <div style={{ color: themeColor, fontSize: 20, fontWeight: 900, letterSpacing: '-0.03em' }}>
              {stat.value}
            </div>
            <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 9, marginTop: 2 }}>
              {stat.label}
            </div>
          </div>
        ))}
      </div>

      {/* Key insights */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {insights.map((fact, i) => (
          <div
            key={i}
            className="fact-slide-in"
            style={{
              animationDelay: `${0.5 + i * 0.12}s`,
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.06)',
              borderRadius: 10,
              padding: '9px 14px',
              fontSize: 12,
              color: 'rgba(255,255,255,0.7)',
            }}
          >
            <span style={{ color: themeColor, marginRight: 8, fontWeight: 700 }}>→</span>
            {fact}
          </div>
        ))}
      </div>

      {/* Stat call-out */}
      {storyboard.stat && (
        <div
          className="stat-card-animate"
          style={{
            animationDelay: '0.8s',
            marginTop: 14,
            padding: '8px 16px',
            borderRadius: 10,
            background: `${themeColor}10`,
            border: `1px solid ${themeColor}30`,
            textAlign: 'center',
            color: `${themeColor}dd`,
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          {storyboard.stat}
        </div>
      )}

      {/* CTA */}
      <div style={{
        marginTop: 14,
        padding: '9px 16px',
        borderRadius: 10,
        background: `${themeColor}12`,
        border: `1px solid ${themeColor}35`,
        textAlign: 'center',
        color: themeColor,
        fontSize: 12,
        fontWeight: 800,
        letterSpacing: '-0.01em',
      }}>
        {storyboard.cta}
      </div>
    </div>
  );
}

// ── Main Smart Visual Wrapper ──────────────────────────────────────────────────
export default function ContentVisual({ storyboard }) {
  const [manualMode, setManualMode] = useState(null);

  if (!storyboard || !storyboard.title) return null;

  const themeColor = storyboard.theme_color || '#7c5efa';
  const autoType = storyboard.visual_type?.toLowerCase() === 'animation' ? 'animation' : 'infographic';
  const mode = manualMode || autoType;
  const totalFrames = 5 * 45; // 5 scenes × 45 frames @ 30fps = 7.5s

  return (
    <div>
      {/* Header row */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {mode === 'animation'
            ? <Film size={13} style={{ color: '#fe2c55', flexShrink: 0 }} />
            : <BarChart2 size={13} style={{ color: themeColor, flexShrink: 0 }} />}
          <span className="text-xs font-bold" style={{ color: mode === 'animation' ? '#fe2c55' : themeColor }}>
            {mode === 'animation' ? 'Remotion Animation' : 'Animated Infographic'}
          </span>
          <span className="text-[9px] px-1.5 py-0.5 rounded-full" style={{
            background: `${themeColor}15`,
            color: themeColor,
            border: `1px solid ${themeColor}25`,
          }}>
            <Sparkles size={8} style={{ display: 'inline', marginRight: 3 }} />
            AI Visual
          </span>
          {autoType !== (manualMode || autoType) && (
            <span className="text-[9px]" style={{ color: 'rgba(255,255,255,0.2)' }}>
              (manually changed)
            </span>
          )}
        </div>
        {/* Toggle mode */}
        <button
          onClick={() => setManualMode(prev => {
            const current = prev || autoType;
            return current === 'animation' ? 'infographic' : 'animation';
          })}
          className="flex items-center gap-1 text-[10px] px-2 py-1 rounded-lg transition-all hover:opacity-80"
          style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.4)', border: '1px solid rgba(255,255,255,0.08)' }}
          data-testid="visual-toggle-btn"
        >
          <RefreshCw size={9} />
          {mode === 'animation' ? 'Switch to Infographic' : 'Switch to Animation'}
        </button>
      </div>

      {/* Visual */}
      {mode === 'animation' ? (
        <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${themeColor}20` }}>
          <Player
            component={BlogAnimation}
            inputProps={{
              title: storyboard.title,
              insight1: storyboard.insight1,
              insight2: storyboard.insight2,
              insight3: storyboard.insight3,
              cta: storyboard.cta,
              stat: storyboard.stat,
              themeColor,
            }}
            durationInFrames={totalFrames}
            fps={30}
            compositionWidth={1280}
            compositionHeight={720}
            style={{ width: '100%', height: 'auto', aspectRatio: '16/9' }}
            controls
            acknowledgeRemotionLicense
            data-testid="remotion-player"
          />
        </div>
      ) : (
        <AnimatedInfographic storyboard={storyboard} themeColor={themeColor} />
      )}

      {/* Visual type reasoning */}
      <div className="mt-1.5 text-[9px] text-center" style={{ color: 'rgba(255,255,255,0.18)' }}>
        Einstein selected: <strong style={{ color: 'rgba(255,255,255,0.3)' }}>{autoType}</strong> for this content
      </div>
    </div>
  );
}
